import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MapPin, Calendar, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import { getCaseById, getAdjacentCases, cases } from '../data/cases';
import { imageUrls } from '../utils/imageOptimizer';
import SEO from '../components/SEO';

export default function CaseDetail() {
  const { id } = useParams();
  const caseItem = getCaseById(id);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  if (!caseItem) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Case ikke fundet</h1>
          <Link to="/cases" className="btn-dark">
            Tilbage til cases
          </Link>
        </div>
      </main>
    );
  }

  const { prev, next } = getAdjacentCases(id);
  const hasMultipleImages = caseItem.gallery && caseItem.gallery.length > 1;
  const showNavigation = cases.length > 1;

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === caseItem.gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? caseItem.gallery.length - 1 : prev - 1
    );
  };

  return (
    <main data-testid="case-detail-page" className="pt-24">
      <SEO 
        title={caseItem.title}
        pathname={`/cases/${id}`}
        description={`${caseItem.title} - ${caseItem.description?.substring(0, 150) || 'Se hvordan NeonSign har hjulpet med professionel skiltning og wayfinding.'}`}
        image={caseItem.gallery?.[0]}
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#F0F0F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/cases" className="text-[#999999] hover:text-[#1A1A1A] transition-colors">
              Cases
            </Link>
            <span className="text-[#E5E5E5]">/</span>
            <span className="text-[#1A1A1A]">{caseItem.title}</span>
          </div>
        </div>
      </div>

      {/* Hero Image Slider */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="relative aspect-[21/9] overflow-hidden group">
            <img
              src={imageUrls.hero(caseItem.gallery[currentImageIndex])}
              alt={`${caseItem.title} - billede ${currentImageIndex + 1}`}
              className="w-full h-full object-cover transition-opacity duration-500"
              data-testid="case-hero-image"
            />
            
            {/* Image Navigation Arrows */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg"
                  aria-label="Forrige billede"
                >
                  <ChevronLeft className="w-6 h-6 text-[#1A1A1A]" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg"
                  aria-label="Næste billede"
                >
                  <ChevronRight className="w-6 h-6 text-[#1A1A1A]" />
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-3 py-1.5 rounded-full">
                  {currentImageIndex + 1} / {caseItem.gallery.length}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Image Thumbnails */}
      {hasMultipleImages && (
        <section className="bg-white py-4">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {caseItem.gallery.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-24 h-16 overflow-hidden transition-all duration-300 ${
                    index === currentImageIndex 
                      ? 'ring-2 ring-[#1A1A1A] opacity-100' 
                      : 'opacity-50 hover:opacity-80'
                  }`}
                >
                  <img
                    src={imageUrls.thumbnail(img)}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Case Info */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left column - Title & Description */}
            <div className="lg:col-span-7">
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-4">
                {caseItem.category}
              </p>
              <h1 
                data-testid="case-title"
                className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-8"
              >
                {caseItem.title}
              </h1>
              <p className="text-[#666666] text-lg leading-relaxed">
                {caseItem.description}
              </p>
            </div>

            {/* Right column - Project Data */}
            <div className="lg:col-span-5">
              <div className="bg-[#F8F8F8] p-8 lg:p-10">
                <h3 className="font-heading text-xs tracking-[0.2em] uppercase text-[#999999] mb-8">
                  Projektdata
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Briefcase className="w-5 h-5 text-[#999999] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#999999] uppercase tracking-wider mb-1">Kunde</p>
                      <p className="text-[#1A1A1A] font-medium">{caseItem.client}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#999999] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#999999] uppercase tracking-wider mb-1">Lokation</p>
                      <p className="text-[#1A1A1A] font-medium">{caseItem.location}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Calendar className="w-5 h-5 text-[#999999] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#999999] uppercase tracking-wider mb-1">År</p>
                      <p className="text-[#1A1A1A] font-medium">{caseItem.year}</p>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="mt-10 pt-8 border-t border-[#E5E5E5]">
                  <p className="text-xs text-[#999999] uppercase tracking-wider mb-4">Leverancer</p>
                  <div className="flex flex-wrap gap-2">
                    {caseItem.services.map((service) => (
                      <span 
                        key={service}
                        className="text-xs text-[#666666] bg-white px-3 py-1.5"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Gallery Grid (for additional images beyond slider) */}
      {caseItem.gallery && caseItem.gallery.length > 3 && (
        <section className="bg-[#F8F8F8] py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <h2 className="font-heading text-2xl font-light text-[#1A1A1A] mb-8">
              Flere billeder
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {caseItem.gallery.slice(3).map((img, index) => (
                <div key={index} className="aspect-[4/3] overflow-hidden">
                  <img
                    src={imageUrls.large(img)}
                    alt={`${caseItem.title} - billede ${index + 4}`}
                    className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Navigation between cases - only show if multiple cases */}
      {showNavigation && (
        <section className="bg-[#F8F8F8] border-t border-[#E5E5E5]">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2">
              {/* Previous Case */}
              <Link 
                to={`/cases/${prev.id}`}
                className="group flex flex-col justify-center p-8 md:p-12 border-r border-[#E5E5E5] hover:bg-[#F0F0F0] transition-colors"
                data-testid="prev-case-link"
              >
                <span className="text-xs text-[#999999] uppercase tracking-wider mb-2 flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Forrige
                </span>
                <span className="font-heading text-lg md:text-xl text-[#1A1A1A] group-hover:text-[#666666] transition-colors">
                  {prev.title}
                </span>
              </Link>

              {/* Next Case */}
              <Link 
                to={`/cases/${next.id}`}
                className="group flex flex-col justify-center items-end p-8 md:p-12 text-right hover:bg-[#F0F0F0] transition-colors"
                data-testid="next-case-link"
              >
                <span className="text-xs text-[#999999] uppercase tracking-wider mb-2 flex items-center gap-2">
                  Næste
                  <ArrowRight className="w-4 h-4" />
                </span>
                <span className="font-heading text-lg md:text-xl text-[#1A1A1A] group-hover:text-[#666666] transition-colors">
                  {next.title}
                </span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#1A1A1A] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-light text-white mb-4">
            Inspireret af dette projekt?
          </h2>
          <p className="text-white/60 mb-8 max-w-md mx-auto">
            Lad os tale om, hvordan vi kan hjælpe med dit projekt.
          </p>
          <Link
            to="/kontakt"
            className="btn-light inline-flex items-center gap-2"
          >
            Kontakt os
          </Link>
        </div>
      </section>
    </main>
  );
}
